import {FC, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import clsx from 'clsx'
import {
  formatInvoiceAmount,
  formatInvoiceCount,
} from '../../../modules/apps/invoice-management/invoices-list/core/_formatters'
import {getInvoiceEntrySourceInfo} from '../../../modules/apps/invoice-management/invoices-list/core/invoiceEntrySourceHelpers'
import {DashboardActivityBatch, DashboardActivitySnapshot} from './core/_models'
import {readDashboardError} from './core/_requests'
import {
  formatAge,
  formatDayLabel,
  formatTimeRange,
  getBatchListUrl,
  getBatchTitle,
  getStatusSegments,
} from './core/dashboardActivityPresentation'
import {useDashboardActivity} from './core/useDashboardData'

type Props = {
  companyId: number
}

const INITIAL_VISIBLE = 8

type DayGroup = {
  day: string
  batches: DashboardActivityBatch[]
}

// Batches arrive newest first; consecutive ones of the same day share a heading.
const groupByDay = (batches: DashboardActivityBatch[]): DayGroup[] =>
  batches.reduce<DayGroup[]>((groups, batch) => {
    const last = groups[groups.length - 1]
    if (last && last.day === batch.day) {
      last.batches.push(batch)
    } else {
      groups.push({day: batch.day, batches: [batch]})
    }
    return groups
  }, [])

const BatchStatusBar: FC<{batch: DashboardActivityBatch}> = ({batch}) => {
  const segments = getStatusSegments(batch.statuses)
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)
  if (total <= 0) return null

  return (
    <div className='mp-activity-bar' aria-hidden='true'>
      {segments.map((segment) => (
        <span
          key={segment.key}
          className={`is-${segment.tone}`}
          style={{flexGrow: segment.count}}
          title={`${formatInvoiceCount(segment.count)} ${segment.label}`}
        />
      ))}
    </div>
  )
}

const BatchRow: FC<{batch: DashboardActivityBatch}> = ({batch}) => {
  const source = getInvoiceEntrySourceInfo(batch.entryType)
  const segments = getStatusSegments(batch.statuses)
  const title = getBatchTitle(batch)

  return (
    <li className={clsx('mp-activity-item', batch.isLive && 'is-live')}>
      <span className={`mp-activity-item__icon is-${source.color}`}>
        <i className={`las ${source.icon}`} aria-hidden='true' />
      </span>
      <div className='mp-activity-item__body'>
        <div className='mp-activity-item__title'>
          <strong>{title}</strong>
          {batch.isLive && (
            <span className='mp-activity-live'>
              <span className='mp-live-pulse' aria-hidden='true' />
              در حال دریافت
            </span>
          )}
        </div>
        <div className='mp-activity-item__meta'>
          <span>{formatTimeRange(batch.fromTime, batch.toTime)}</span>
          {batch.userName && <span>توسط {batch.userName}</span>}
          <span>جمع {formatInvoiceAmount(batch.netAmountExact ?? batch.netAmount)} ریال</span>
          {!batch.isLive && <span>{formatAge(batch.ageSeconds)}</span>}
        </div>
        <BatchStatusBar batch={batch} />
        <ul className='mp-activity-item__statuses' aria-label='وضعیت ارسال این دسته'>
          {segments.map((segment) => (
            <li key={segment.key}>
              <Link
                to={getBatchListUrl(batch, segment.listKey)}
                className={`mp-status-chip is-${segment.tone}`}
                title={`مشاهده صورتحساب‌های «${segment.label}» این دسته در فهرست`}
              >
                <i className={`las ${segment.icon}`} aria-hidden='true' />
                <strong>{formatInvoiceCount(segment.count)}</strong>
                {segment.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <Link
        to={getBatchListUrl(batch)}
        className='mp-activity-item__open'
        aria-label={`مشاهده صورتحساب‌های این دسته (${title}) در فهرست`}
        title='مشاهده در فهرست صورتحساب‌ها'
      >
        <i className='las la-angle-left' aria-hidden='true' />
      </Link>
    </li>
  )
}

const ActivityHeading: FC<{
  snapshot?: DashboardActivitySnapshot
  refreshing: boolean
  onRefresh: () => void
}> = ({snapshot, refreshing, onRefresh}) => (
  <div className='mp-panel__head'>
    <div>
      <span>گزارش فعالیت‌ها</span>
      <h2 id='mp-activity-title'>آخرین ورود و صدور صورتحساب‌ها</h2>
      <small className='mp-panel__note'>
        هر ردیف یک دسته است: صورتحساب‌های یک روش ثبت که بدون وقفهٔ بیش از{' '}
        {formatInvoiceCount(snapshot?.batchGapMinutes || 30)} دقیقه ثبت شده‌اند، با وضعیت ارسال
        فعلی.
      </small>
    </div>
    <button
      type='button'
      className='btn btn-icon btn-light mp-panel__refresh'
      onClick={onRefresh}
      disabled={refreshing}
      aria-label='به‌روزرسانی گزارش فعالیت‌ها'
      title='به‌روزرسانی'
    >
      <i className={clsx('las la-sync', refreshing && 'mp-spin')} aria-hidden='true' />
    </button>
  </div>
)

const DashboardActivityFeed: FC<Props> = ({companyId}) => {
  const {data, isLoading, isError, error, refetch, refresh, refreshing} =
    useDashboardActivity(companyId)
  const [expanded, setExpanded] = useState(false)

  const batches = useMemo(() => data?.batches || [], [data?.batches])
  const visible = expanded ? batches : batches.slice(0, INITIAL_VISIBLE)
  const groups = useMemo(() => groupByDay(visible), [visible])

  return (
    <section
      className={clsx('mp-panel mp-activity', {'is-refreshing': refreshing && !isLoading})}
      aria-labelledby='mp-activity-title'
      aria-busy={isLoading}
    >
      <ActivityHeading snapshot={data} refreshing={refreshing} onRefresh={() => refresh()} />

      {isLoading ? (
        <ul className='mp-activity-list' aria-hidden='true'>
          {[0, 1, 2, 3].map((placeholder) => (
            <li key={placeholder} className='mp-activity-item is-loading' />
          ))}
        </ul>
      ) : isError && !data ? (
        <div className='mp-dashboard-alert' role='alert'>
          <i className='las la-exclamation-circle' aria-hidden='true' />
          <span>{readDashboardError(error, 'گزارش فعالیت‌ها دریافت نشد.')}</span>
          <button type='button' className='btn btn-sm btn-light-primary' onClick={() => refetch()}>
            تلاش مجدد
          </button>
        </div>
      ) : batches.length === 0 ? (
        <div className='mp-dashboard-empty mp-dashboard-empty--wide'>
          در {formatInvoiceCount(data?.windowDays || 30)} روز گذشته صورتحسابی برای این کسب‌وکار ثبت
          نشده است.
        </div>
      ) : (
        <>
          {groups.map((group) => (
            <div key={group.day} className='mp-activity-day'>
              <h3 className='mp-activity-day__title'>
                {formatDayLabel(group.day, data?.today, data?.yesterday)}
              </h3>
              <ol className='mp-activity-list'>
                {group.batches.map((batch) => (
                  <BatchRow key={batch.key} batch={batch} />
                ))}
              </ol>
            </div>
          ))}
          <div className='mp-activity__footer'>
            {batches.length > INITIAL_VISIBLE && (
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
              >
                {expanded
                  ? 'نمایش کمتر'
                  : `نمایش ${formatInvoiceCount(batches.length - INITIAL_VISIBLE)} دستهٔ دیگر`}
              </button>
            )}
            <span>
              {data?.windowLimited
                ? `فقط ${formatInvoiceCount(50000)} صورتحساب اخیر بررسی شده است.`
                : `فعالیت‌های ${formatInvoiceCount(data?.windowDays || 30)} روز گذشته`}
            </span>
            <Link to='/apps/log-management/logs'>
              همه گزارش‌های عملیات
              <i className='las la-angle-left' aria-hidden='true' />
            </Link>
          </div>
        </>
      )}
    </section>
  )
}

export {DashboardActivityFeed}
